import { isEmailShaped } from './share-flow';

// The share dialog's people field, as rules with no DOM — the same split `share-flow.ts` and
// `app-lending-flow.ts` already make, and for the same reason: three surfaces have to rank the same
// people the same way, and a rule invented three times drifts with only one copy getting the next fix.
//
// The field replaces a bare `<input type="email">` on a product that holds the user's contacts. What
// it must get right is not the typeahead; it is what a row is allowed to CLAIM about a person, and
// whether pressing it can actually work.
//
// ## A grant target is an EMAIL. A phone is something you search by.
//
// The whole share path is email-only: `pages/api/{file,app}-shares.ts` answer `400 invalid_email`
// before the network, `SymphonyBffClient.shareFile/shareApp` take `granteeEmail`, and the service
// calls `getOrCreateRecipient` with `.setEmail(...)`. So a phone number is not a target, and a row
// offering one is a row that fails on press — the same defect as a contact with no way to reach them,
// which this module already refuses to render.
//
// Phones still MATCH (typing a number finds the person who owns it), because that is how people look
// each other up. They are never the identifier a grant is made against. A contact reachable only by
// phone is therefore not offered at all, and that is a product gap to close in the share path, not
// something a picker can paper over.
//
// ## Three badges, and a fourth that is a deliberate silence
//
// `symphony` — this address has an account. `contact` — they are in your People, which is a fact
// about the SHARER. `unknown` — a freely typed address, about which nothing is claimed. And
// `unresolved` — nobody answered, so nothing at all is said.
//
// The membership question is settled by an ADR in the VMR
// (`docs/2026-08-29-share-recipient-membership-disclosure.md`): disclosure is ACCEPTED, so the
// `symphony` badge is legitimate — from a resolver that is capped and rate-limited, and only as a
// genuinely tri-valued answer.
//
// That tri-value is why `membership` is a REQUIRED discriminated value rather than an optional
// boolean. The predicates behind it (`isClaimedSymphonyUser`, `isOwnersContact`) both fail toward
// TRUE by design — right for a gate, and a lie as a badge, because a blipped read would paint a
// stranger "on Symphony", which is exactly the assurance somebody acts on before handing over a
// document. With an optional boolean, a caller who simply forgets to resolve renders `unknown` —
// "we checked, they are a stranger" — silently, with no type error and no failing test. Required and
// discriminated, the compiler asks the question.

/** What is known about one person's Symphony account. `unresolved` includes a 429 from the cap. */
export type Membership = { status: 'member' } | { status: 'notMember' } | { status: 'unresolved' };

/** Nobody has answered — in flight, refused by the rate limit, or a client older than the resolver. */
export const UNRESOLVED: Membership = { status: 'unresolved' };

/**
 * A resolver's answer as a `Membership`.
 *
 * `undefined` — the wire field absent, a map with no such key — is `unresolved`, and this is the one
 * place that conversion happens so no call site can turn "we did not find out" into "not a member" by
 * reaching for a `??  false`.
 */
export function membershipOf(hasAccount: boolean | undefined): Membership {
  if (hasAccount === undefined) return UNRESOLVED;
  return hasAccount ? { status: 'member' } : { status: 'notMember' };
}

/**
 * A person the field can offer, in the shape both contact sources actually return.
 *
 * `pages/api/contacts.ts` (web) and the `contacts-search` native-data action return the same rows and
 * both drop anybody with no name and no identifier, so these are required rather than optional —
 * defensive optionality here would only hide a source that changed.
 */
export interface RecipientCandidate {
  id: string;
  name: string;
  /** `emails[0]` is the CRM's `primaryEmail`, or a stored person's first address — which is why
   *  "first untaken email" is the right pick either way. */
  emails: string[];
  /** Matched against, never shared against. See the header. */
  phones: string[];
}

/** What the field holds once somebody has been picked. */
export interface PickedRecipient {
  /** Absent for a freely typed address, which has no contact behind it. */
  id?: string;
  name: string;
  /** The EMAIL the grant will be made against. */
  identifier: string;
  /** Required, and discriminated, so "not told" cannot masquerade as "told no". See the header. */
  membership: Membership;
  /** True when they came out of the caller's own People. A fact about the SHARER. */
  isContact: boolean;
}

export type RecipientBadge = 'symphony' | 'contact' | 'unknown' | 'unresolved';

/**
 * What a row may claim.
 *
 * Two facts, and only one of them needs asking. Whether somebody is in the caller's own People is
 * known locally and is always sayable; whether they hold a Symphony account is a question for
 * `ResolveShareRecipients`, which nothing asks yet.
 *
 * So an UNRESOLVED contact is still `contact` — that is the whole of what the field claims today. It
 * used to answer `unresolved` first, which made the badge unreachable on every surface (every person
 * is constructed unresolved by design) while three file headers said the field tells you whether
 * somebody is one of your contacts. It did not.
 *
 * What `unresolved` must still never do is fall through to `unknown`: for a NON-contact, "we did not
 * ask" and "we asked and they are a stranger" are different sentences, and only the second is a
 * claim we have earned.
 */
export function badgeFor(person: PickedRecipient): RecipientBadge {
  if (person.membership.status === 'member') return 'symphony';
  if (person.isContact) return 'contact';
  return person.membership.status === 'unresolved' ? 'unresolved' : 'unknown';
}

/**
 * The notices to show after a run, folded onto what earlier runs left.
 *
 * A resumed run answers only the people it actually attempted, so REPLACING the list throws away the
 * notices of everybody who landed before the question that stopped it — and the first casualty is the
 * invite line, which this module calls the only moment a mistyped address is ever observable. Later
 * wins for the same person (a repeat attempt's verdict is the current one); first-seen order is kept
 * so the list does not reshuffle under the reader between presses.
 */
export function mergeOutcomes<T extends { identifier: string }>(previous: T[], next: T[]): T[] {
  const byIdentifier = new Map<string, T>();
  for (const outcome of [...previous, ...next]) byIdentifier.set(normEmail(outcome.identifier), outcome);
  return Array.from(byIdentifier.values());
}

/** Whether a typed value can be shared with. Email only — see the header. */
export function isShareableIdentifier(value: string): boolean {
  return isEmailShaped(value.trim());
}

/**
 * One person's identity for exclusion purposes.
 *
 * Phones are reduced to their digits because `toSummary` deliberately carries BOTH `primaryPhone` and
 * `e164Phone`, which are the same number written two ways — `+972545550134` and `054-555-0134` dedupe
 * to nothing as strings, so without this the same human is offered twice.
 */
const normEmail = (v: string) => v.trim().toLowerCase();
const normPhone = (v: string) => v.replace(/\D/g, '');

/** Everything a query may match against, normalised. */
function haystack(c: RecipientCandidate): string[] {
  return [...c.emails.map(normEmail), ...c.phones.map(normPhone)].filter(Boolean);
}

/** The emails a grant could be made against, normalised and deduped. */
function shareableEmails(c: RecipientCandidate): string[] {
  // Email-SHAPED, not merely present. A CRM row can carry "n/a" or a half-typed address in its email
  // field, and offering it produces a row that looks like every other contact and fails the moment it
  // is pressed — the header's whole point is that this module never renders what cannot be granted.
  return Array.from(new Set(c.emails.map(normEmail).filter((e) => e && isShareableIdentifier(e))));
}

/**
 * The address this contact would be shared against: their first email that is NOT already taken.
 *
 * Not simply the first email. Exclusion admits a contact while any address of theirs is free, so
 * keying the row on the primary one re-offers exactly the address that already holds the grant —
 * picking it would re-add what is already there.
 */
function untakenEmail(c: RecipientCandidate, taken: Set<string>): string | null {
  return shareableEmails(c).find((e) => !taken.has(e)) ?? null;
}

/** One row the field offers. `literal` is the "use what you typed" row, with no contact behind it. */
export type SuggestionRow =
  | { kind: 'contact'; candidate: RecipientCandidate; identifier: string }
  | { kind: 'literal'; identifier: string };

/** Whether the contacts read has answered. A failed read is NOT an empty address book. */
export type ContactsStatus = 'loading' | 'ready' | 'error';

export interface RankInput {
  /** What has been typed. Empty asks for the recent-contacts default shown before typing. */
  query: string;
  contacts: RecipientCandidate[];
  /** Where the contacts came from. `share-flow.ts` keeps the same three states apart, for the same reason. */
  contactsStatus?: ContactsStatus;
  /** Emails that already hold a grant — offering one again is offering a no-op. */
  alreadyHasAccess: string[];
  /** Emails already picked in this send. */
  pending: string[];
  /** The owner's own addresses. Sharing with yourself is not a share. */
  self?: string[];
  limit?: number;
}

const DEFAULT_LIMIT = 6;

/**
 * The rows to offer: the caller's own people first, then the literal address they typed.
 *
 * Rows are deduped by the address they would share against, so two contacts holding the same email
 * cannot both be offered — picking either makes the identical grant.
 */
export function rankSuggestions({
  query,
  contacts,
  alreadyHasAccess,
  pending,
  self = [],
  limit = DEFAULT_LIMIT,
}: RankInput): SuggestionRow[] {
  const taken = new Set([...alreadyHasAccess, ...pending, ...self].map(normEmail));
  const q = query.trim().toLowerCase();
  const qDigits = normPhone(query);

  const rows: SuggestionRow[] = [];
  const offered = new Set<string>();

  for (const candidate of contacts) {
    if (rows.length >= limit) break;
    // A contact with no shareable email cannot be granted anything today — a row that fails on press
    // is worse than a shorter list. Includes the phone-only contact, deliberately.
    const identifier = untakenEmail(candidate, taken);
    if (!identifier || offered.has(identifier)) continue;
    if (q) {
      const matches = candidate.name.toLowerCase().includes(q) || haystack(candidate).some((h) => h.includes(q) || (qDigits.length >= 3 && h.includes(qDigits)));
      if (!matches) continue;
    }
    offered.add(identifier);
    rows.push({ kind: 'contact', candidate, identifier });
  }

  // The literal row appears only for a shareable address that is not already on screen — otherwise it
  // is a second way to add somebody the list is already offering.
  const raw = query.trim();
  const rawNorm = normEmail(raw);
  if (raw && isShareableIdentifier(raw) && !taken.has(rawNorm) && !offered.has(rawNorm)) {
    rows.push({ kind: 'literal', identifier: raw });
  }
  return rows;
}

/**
 * Why the field has nothing to offer. Five different things to say, not one empty list.
 *
 * `loading` / `readFailed` come first because a contacts read that has not answered is not an empty
 * address book — the same distinction `share-flow.ts:accessLine` keeps between "private" and "we
 * could not find out", and for the same reason: rendering a failed read as a result is a claim.
 */
export type EmptyReason = 'loading' | 'readFailed' | 'typeMore' | 'alreadyAdded';

export function emptyReason(input: RankInput): EmptyReason | null {
  // NOTHING TYPED IS NOT AN EMPTY RESULT. The field offers no list until it has been asked a
  // question, so before the first keystroke there is no answer to explain — the placeholder is the
  // whole of what to say, and a line under an untouched field reads as a problem with it.
  const raw = input.query.trim();
  if (!raw) return null;

  const status = input.contactsStatus ?? 'ready';
  if (status === 'loading') return 'loading';
  if (status === 'error') return 'readFailed';
  if (rankSuggestions(input).length > 0) return null;

  // Everything typed that could be shared with produces a literal row, so reaching here with a
  // shareable address means it is already taken — "already added", never "no match".
  if (isShareableIdentifier(raw)) return 'alreadyAdded';
  return 'typeMore';
}

/**
 * The ADR caps a resolve at ten identifiers per request, and the cap is part of the decision rather
 * than a nice-to-have — so it lives here, where every surface reads it, not in three callers.
 */
export const RESOLVE_BATCH_LIMIT = 10;

export function resolveBatches(identifiers: string[]): string[][] {
  const unique = Array.from(new Set(identifiers.map(normEmail).filter(Boolean)));
  const batches: string[][] = [];
  for (let i = 0; i < unique.length; i += RESOLVE_BATCH_LIMIT) batches.push(unique.slice(i, i + RESOLVE_BATCH_LIMIT));
  return batches;
}

/**
 * The order N grants are made in, and why it is not one `Promise.all`.
 *
 * A FILE older than the `FileSystem.File.id` field has no id, and the server mints one at share time:
 * `FileSharing.shareFile` calls `ensureFileId`, which reads the file, sees no id, mints a UUID and
 * writes it back. The write itself is not the problem — it is the read-modify-write ACROSS two RPCs,
 * which is a lost update on the `id` field. Two concurrent shares both read id-less, both mint, and
 * the later write wins: every earlier grant is keyed on an id the file no longer has, so those
 * grantees see nothing and the OWNER's share list comes back empty — which the dialog renders as
 * "Private" about the people it just shared with.
 *
 * `ensureFileId` is idempotent once an id exists, so it is enough for the first grant to WRITE one
 * before the rest start; threading the returned `file_id` is an optimisation, not the fix.
 *
 * Two things this does NOT solve, and neither should be implied:
 *  - a first grant that answered `requiresConfirmation` wrote nothing and minted nothing (the mint is
 *    deliberately after that gate), so `rest` must be gated on a first grant that actually landed —
 *    hence `releaseRest`, not "the first one returned".
 *  - a concurrent share of the same file from another tab or phone. Only the server can close that.
 *
 * APP shares carry no such hazard — nothing is minted, and the route 400s without an `appId` — so the
 * subject kind is stated rather than inferred from an absent id.
 */
export type ShareSubjectKind = 'file' | 'app';

export interface GrantPlan<T> {
  first: T;
  rest: T[];
  /** True when the first grant may MINT the subject's id, so the rest must wait for it to land. */
  mustSerialize: boolean;
}

export function planGrants<T>(recipients: T[], subjectKind: ShareSubjectKind, subjectId: string | undefined): GrantPlan<T> | null {
  if (recipients.length === 0) return null;
  const [first, ...rest] = recipients;
  return { first, rest, mustSerialize: subjectKind === 'file' && !subjectId };
}

/** What the first grant answered, as far as the plan cares. */
/**
 * Whether a stored "yes, share with that stranger" still applies to the attempt about to run.
 *
 * The answer is given about ONE address. The list underneath it stays editable while the question is
 * on screen — removing the very chip being asked about is a reasonable thing to do — so without this
 * the owner's yes silently transfers to whoever the list now begins with, and the unknown-address gate
 * is bypassed for somebody they were never asked about. A mismatch is not an error: the run simply
 * goes unconfirmed and the server asks again, about the right person.
 */
export function confirmAppliesTo(confirmed: string | null | undefined, first: string): boolean {
  const c = normEmail(confirmed ?? '');
  return c !== '' && c === normEmail(first);
}

export type FirstGrantOutcome = 'wrote' | 'askedFirst' | 'failed';

/**
 * Whether the remaining grants may start.
 *
 * Only a first grant that actually WROTE has minted the id the rest depend on. A confirmation
 * question wrote nothing; a failure wrote nothing. Releasing on either puts the race straight back.
 */
export function releaseRest(plan: GrantPlan<unknown>, outcome: FirstGrantOutcome): boolean {
  if (!plan.mustSerialize) return true;
  return outcome === 'wrote';
}
